'use client';

import { useCallback, useEffect, useRef } from 'react';
import { MdCheckCircle, MdInfo, MdError } from 'react-icons/md';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { ButtonType, ButtonSize } from '@/components/ui/button/Button.types';
import { Colors } from '@/lib/colorUtils';
import { ToastVariant, type ToastProps } from './Toast.types';
import {
  toastContainerClasses,
  toastBodyClasses,
  toastDividerClasses,
  toastProgressTrackClasses,
  toastIconClasses,
  toastProgressBarClasses,
} from './Toast.style';

const ICON_SIZE = 16;

const VARIANT_ICON = {
  [ToastVariant.success]: MdCheckCircle,
  [ToastVariant.info]: MdInfo,
  [ToastVariant.error]: MdError,
};

export const Toast: React.FC<ToastProps> = ({
  id,
  variant,
  message,
  durationMs,
  action,
  icon,
  onDismiss,
}) => {
  const DefaultIcon = VARIANT_ICON[variant];

  // Mirror onDismiss into a ref so the timer doesn't reset on parent re-render.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  // Hover pauses the countdown; the progress bar pauses via group-hover CSS.
  // Track remaining time so resume continues rather than restarts.
  const remainingRef = useRef(durationMs);
  const startRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTimer = useCallback(() => {
    startRef.current = Date.now();
    timerRef.current = setTimeout(() => dismissRef.current(id), remainingRef.current);
  }, [id]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  const handleMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startRef.current;
  };

  const handleMouseLeave = () => {
    startTimer();
  };

  return (
    <div
      className={toastContainerClasses}
      role="status"
      aria-live="polite"
      data-cy="toast"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={toastBodyClasses}>
        <span className={toastIconClasses({ variant })} data-cy="toast-icon" aria-hidden>
          {icon ?? <DefaultIcon size={ICON_SIZE} />}
        </span>
        <Text size="14" color={Colors.colorFont} data-cy="toast-message">
          {message}
        </Text>
        {action && (
          <>
            <span className={toastDividerClasses} aria-hidden />
            <Button
              buttonType={ButtonType.text}
              size={ButtonSize.small}
              onClick={action.onClick}
              data-cy="toast-action"
            >
              {action.label}
            </Button>
          </>
        )}
      </div>
      <div className={toastProgressTrackClasses}>
        <div
          className={toastProgressBarClasses({ variant })}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (progress animation duration)
          style={{ animationDuration: `${durationMs}ms` }}
          data-cy="toast-progress"
        />
      </div>
    </div>
  );
};

Toast.displayName = 'Toast';
